// src/lib/imageUpload.js
// Uploads player photos to Firebase Storage.
//
// Images are resized IN THE BROWSER before upload. This matters for three reasons:
//   1. Storage/bandwidth cost — a 2.5MB phone photo becomes ~250KB
//   2. Page speed on slow connections (a real concern for our Caribbean audience)
//   3. Upload time for the person adding the player
//
// Roster cards render around 400px wide, so 1200px covers retina with room to spare.

import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../firebase";

const MAX_DIMENSION = 1200;
const JPEG_QUALITY = 0.85;
const MAX_SOURCE_BYTES = 15 * 1024 * 1024; // reject absurd files before we even decode

const BUCKET = "primevalor-8faa0.firebasestorage.app";

// Each upload folder has its own same-domain route (see public/_redirects).
const PROXY_ROUTES = {
  players: "/player-photos",
  announcements: "/announcement-photos",
};

/**
 * The URL an uploaded image should be displayed from.
 *
 * Images are stored in a publicly readable Cloud Storage bucket, but they're
 * shown through our own domain (Netlify edge proxies, see PROXY_ROUTES) —
 * some visitors run ad/content blockers that block googleapis.com wholesale,
 * and images served from there simply vanish for them.
 *
 * Note the tokened firebasestorage download URL saved on old records is only
 * served to browsers (verified: CLI and server clients get 404 from it), which
 * is why the proxy targets the plain public URL instead. The file name is
 * derived from imagePath, or recovered from the old download URL for records
 * that predate imagePath.
 *
 * Local development talks to the bucket directly — the Netlify proxy doesn't
 * exist on localhost, and blockers aren't a concern in dev.
 */
export function displayableImageUrl(imageUrl, imagePath) {
  let folder = null;
  let file = null;

  const fromPath = (imagePath || "").match(/^(players|announcements)\/(.+)$/);
  if (fromPath) {
    [, folder, file] = fromPath;
  } else if (imageUrl && imageUrl.includes("firebasestorage.googleapis.com")) {
    const m = imageUrl.match(/\/o\/(players|announcements)%2F([^?]+)/i);
    if (m) {
      folder = m[1];
      file = decodeURIComponent(m[2]);
    }
  }
  if (!folder || !file) return imageUrl || null; // not an uploaded image — leave untouched

  const isLocal =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  if (isLocal) {
    return `https://storage.googleapis.com/${BUCKET}/${folder}/${encodeURIComponent(file)}`;
  }
  return `${PROXY_ROUTES[folder]}/${encodeURIComponent(file)}`;
}

/** Human-readable size, e.g. "1.4 MB" */
export function formatBytes(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Decode a File into something canvas can draw, respecting EXIF rotation. */
async function decodeImage(file) {
  // createImageBitmap honours EXIF orientation, so phone photos aren't sideways.
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Safari fallback below
    }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image file."));
    };
    img.src = url;
  });
}

/**
 * Resize an image File down to MAX_DIMENSION on its longest side.
 * Returns { blob, width, height, originalBytes, resizedBytes }.
 * Images already smaller than the cap are still re-encoded, which strips
 * bulky camera metadata and normalises the format to JPEG.
 */
export async function resizeImage(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error("That file isn't an image. Please choose a JPG or PNG.");
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error(
      `That image is ${formatBytes(file.size)}, which is too large. Please use one under 15 MB.`
    );
  }

  const source = await decodeImage(file);
  const { width: sw, height: sh } = source;

  const scale = Math.min(1, MAX_DIMENSION / Math.max(sw, sh));
  const width = Math.round(sw * scale);
  const height = Math.round(sh * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(source, 0, 0, width, height);
  if (typeof source.close === "function") source.close();

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Could not process that image."))),
      "image/jpeg",
      JPEG_QUALITY
    )
  );

  return {
    blob,
    width,
    height,
    originalBytes: file.size,
    resizedBytes: blob.size,
  };
}

/** Build a collision-proof storage path without leaking the original filename. */
function buildPath(folder, name, seed) {
  const slug =
    (name || "upload")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "upload";
  return `${folder}/${slug}-${seed}.jpg`;
}

/**
 * Resize then upload an image into one of the allowed folders.
 * Returns { url, path, width, height, originalBytes, resizedBytes }.
 * Keep `path` on the record so the old file can be deleted on replace.
 */
export async function uploadImage(file, name, { seed, folder = "players" } = {}) {
  if (!PROXY_ROUTES[folder]) throw new Error(`Unknown upload folder: ${folder}`);
  const resized = await resizeImage(file);
  // Caller passes a seed (e.g. Date.now()) so repeat uploads never collide.
  const path = buildPath(folder, name, seed ?? Math.floor(Math.random() * 1e9));
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, resized.blob, {
    contentType: "image/jpeg",
    cacheControl: "public, max-age=31536000, immutable",
  });
  const url = await getDownloadURL(storageRef);

  return { url, path, ...resized, blob: undefined };
}

/** Resize then upload a player photo. */
export function uploadPlayerImage(file, playerName, opts = {}) {
  return uploadImage(file, playerName, { ...opts, folder: "players" });
}

/** Delete a previously uploaded photo. Safe to call with a missing path. */
export async function deletePlayerImage(path) {
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    // Already gone, or never existed — not worth surfacing to the user.
    if (err?.code !== "storage/object-not-found") {
      console.warn("Could not delete old image:", err);
    }
  }
}
